import * as React from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { JsonSchema, ConfigUiHints } from '../types'
import { humanize, hintForPath } from '../utils/hint-for-path'
import { SchemaForm } from './schema-form/SchemaForm'

type Props = {
  categoryKey: string
  schema: JsonSchema
  config: Record<string, unknown>
  hints: ConfigUiHints
  disabled?: boolean
  onChange: (path: Array<string | number>, val: unknown) => void
}

const Accordion = styled.div`
  background: ${(p) => p.theme.inputBack};
  border-radius: ${(p) => p.theme.radius};
  margin-bottom: 12px;
  overflow: hidden;
`

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  color: ${(p) => p.theme.white};
  padding: 16px 20px;
  cursor: pointer;
  text-align: left;
  font-size: 16px;
  font-family: 'NunitoSansSemiBold';
  min-height: 44px;
  gap: 12px;
  flex-wrap: wrap;

  &:hover {
    background: ${(p) => p.theme.selectorBack};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${(p) => p.theme.accent};
    outline: none;
  }

  @media (max-width: ${screenSizes.XS}px) {
    padding: 14px 16px;
    font-size: 15px;
  }
`

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 12px;
  transition: transform 0.15s;
  transform: ${(p) => p.$open ? 'rotate(90deg)' : 'rotate(0)'};
  flex-shrink: 0;
`

const Description = styled.span`
  color: ${(p) => p.theme.listDesc};
  font-size: 12px;
  font-family: 'NunitoSans';
  flex: 1;
  text-align: right;

  @media (max-width: ${screenSizes.XS}px) {
    display: none;
  }
`

const Body = styled.div`
  padding: 4px 20px 20px;

  @media (max-width: ${screenSizes.XS}px) {
    padding: 4px 16px 16px;
  }
`

export default function ConfigCategory({ categoryKey, schema, config, hints, disabled, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const categorySchema = schema.properties?.[categoryKey]
  const categoryConfig = (config?.[categoryKey] as Record<string, unknown>) ?? {}
  const hint = hintForPath([categoryKey], hints)
  const label = hint?.label ?? humanize(categoryKey)
  const description = hint?.help ?? ''

  if (!categorySchema) return null

  return (
    <Accordion>
      <Header type="button" onClick={() => setOpen((v) => !v)}>
        <span>
          <Chevron $open={open}>{'\u25B6'}</Chevron>
          {' '}{label}
        </span>
        {description && <Description>{description}</Description>}
      </Header>
      {open && (
        <Body>
          <SchemaForm
            schema={categorySchema}
            value={categoryConfig}
            hints={hints}
            path={[categoryKey]}
            disabled={disabled}
            onChange={onChange}
          />
        </Body>
      )}
    </Accordion>
  )
}
